import { head, propEq } from 'ramda'
import { parse } from '@datasert/cronjs-parser'
import { useTranslation } from 'react-i18next'
import React, { Fragment } from 'react'

import { Grid } from '../../packages/kiti/components/structure/Grid'
import { formatConditionValue } from '../../utils/helpers/conditions'
import { getGenderContext } from '../../utils/helpers/gender'
import { truncateString } from './../../packages/kiti/utils/helpers/string'
import { usePlaceTypes } from '../../utils/helpers/placeTypes'
import Text from '../../packages/kiti/components/presentation/Text'
import useTranslatedOrOriginalText from './../../utils/hooks/translate'

const FrequencySentence = ({ trigger }) => {
  const { t } = useTranslation(['triggers', 'common'])
  const { event } = trigger
  const frequency = head(parse(trigger.frequency || '* * * * *')?.expressions)

  const timeValue =
    frequency?.hour?.values &&
    String(head(frequency?.hour?.values || [])).padStart(2, '0') +
      ':' +
      String(head(frequency?.minute?.values || [])).padStart(2, '0')

  return (
    <Text bold size="p">
      {t('periods.' + event)} as {timeValue}
    </Text>
  )
}

const OPTIONS = [
  { name: '1 hora', value: 60 },
  { name: '2 horas', value: 120 },
  { name: '3 horas', value: 180 },
  { name: '4 horas', value: 240 },
  { name: '5 horas', value: 300 },
  { name: '6 horas', value: 360 },
  { name: '7 horas', value: 420 },
  { name: '8 horas', value: 480 },
  { name: '9 horas', value: 540 },
  { name: '10 horas', value: 600 },
  { name: '11 horas', value: 660 },
  { name: '12 horas', value: 720 },
  { name: '13 horas', value: 780 },
  { name: '14 horas', value: 840 },
  { name: '15 horas', value: 900 },
  { name: '16 horas', value: 960 },
  { name: '17 horas', value: 1020 },
  { name: '18 horas', value: 1080 },
  { name: '19 horas', value: 1140 },
  { name: '20 horas', value: 1200 },
  { name: '21 horas', value: 1260 },
  { name: '22 horas', value: 1320 },
  { name: '23 horas', value: 1380 },
  { name: '24 horas', value: 1440 }
]

const FIELDS = [{ name: 'agendamento', value: 'scheduling' }]

const RelativeSentence = ({ trigger }) => {
  const { t } = useTranslation(['triggers', 'common'])
  const { event, configuration } = trigger
  const fieldName = FIELDS.find(propEq('value', configuration.field))?.name

  if (event === 'relative_moment') {
    return (
      <Text bold size="p" underline>
        {t('relatives.relative_moment')} {fieldName}
      </Text>
    )
  } else {
    const timeName = OPTIONS.find(propEq('value', configuration.time))?.name

    return (
      <Text bold size="p" underline>
        {timeName} {t('relatives.' + event)} {fieldName}
      </Text>
    )
  }
}

const Sentence = ({ trigger }) => {
  const { t } = useTranslation(['triggers', 'common'])
  const { getTranslatedOrOriginalText } = useTranslatedOrOriginalText(['triggers', 'common'])
  const { entity, conditions, event, action } = trigger
  const context = getGenderContext(entity)
  const isPeriodic = trigger.event?.startsWith('every_')
  const isRelative = trigger.event?.startsWith('relative_')
  const [placeTypes] = usePlaceTypes()

  return (
    <Grid gap={0.3} align="center" wrap>
      {isRelative && <RelativeSentence trigger={trigger} />}
      {isPeriodic && <FrequencySentence trigger={trigger} />}

      {!isRelative && !isPeriodic && (
        <>
          <Text bold size="p">
            {t('when', { context })}
          </Text>

          <Text bold size="p" underline>
            {t('entities.' + entity, placeTypes.find(propEq('key', entity))?.name)} for{' '}
            {t('events.' + event, { context })}
          </Text>
        </>
      )}
      {conditions?.map((condition, index) => (
        <Fragment key={index}>
          <Text bold size="p">
            {index === 0 ? 'se' : 'e'}
          </Text>
          <Text bold size="p" underline>
            {t('fields.' + condition.field)}
          </Text>
          <Text bold size="p">
            for
          </Text>
          <Text bold size="p">
            {t('common:conditions.operators.' + (condition.operator || 'eq')).toLowerCase()}
          </Text>
          <Text bold size="p" underline>
            {truncateString(getTranslatedOrOriginalText('status.', formatConditionValue(condition)), 25)}
          </Text>
        </Fragment>
      ))}
      <Text bold size="p">
        deve
      </Text>
      <Text bold size="p" underline>
        {t('actions.' + action)}
      </Text>
    </Grid>
  )
}

export default Sentence
